'use client'
import { GetDrupalPageFromPathResponse, NoDrupalPageInPath } from "@/lib/drupal/drupal.server";
import { ExternalResourceInterface } from "@/lib/drupal/external-resource";
import { DrupalNode } from "@/types";
import { createContext, ReactNode, useContext, useState, useEffect } from "react";
/**
 * @file
 * RedirectProvider context provides
 * redirects to the frontend so they can be issued
 * with query hashes maintained on them.
 * 
 * @see app/[...slug]/Page
 * @see components/app/drupal/PageContent
 */

interface ContentRouteInterface<T extends DrupalNode, R extends ExternalResourceInterface = ExternalResourceInterface> extends GetDrupalPageFromPathResponse<T, R> {
  type: T['type']
}

interface NoContentRouteInterface extends NoDrupalPageInPath {
  type?: undefined
}

type UseContentFromRouteReturnType<T extends DrupalNode = DrupalNode, R extends ExternalResourceInterface = ExternalResourceInterface> = ContentRouteInterface<T, R> | NoContentRouteInterface

const ContentRouteContext = createContext<UseContentFromRouteReturnType>({})

export function useContentFromRoute<T extends DrupalNode, R extends ExternalResourceInterface = ExternalResourceInterface>(): UseContentFromRouteReturnType<T, R> {
  return useContext(ContentRouteContext) as UseContentFromRouteReturnType<T, R>
}

export const ContentRouteProvider = <T extends DrupalNode>(
  {children, ...context}: UseContentFromRouteReturnType<T> & {
    children: ReactNode,
  }) => {
  const [isProductNavigationPresent, setIsProductNavigationPresent] = useState(false);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Check for Product Navigation
      const found = document.querySelector('#productNavigation') !== null;

      setIsProductNavigationPresent(found);
    }
  }, []);

  return <ContentRouteContext.Provider value={context}>
    {children}
    {isProductNavigationPresent && (
      <a href={`#productNavigation`} className="sr-only focus:not-sr-only">
        Back to Product navigation
      </a>
    )}

  </ContentRouteContext.Provider>
}